import { ChangeEvent, FunctionComponent, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import { AxiosError } from 'axios';
import Button from '../button';
import ClaimsTrackerClient from '@shared/clients/ClaimsTrackerClient';
import {
  AnalysisTransitionOption,
  StageIds,
  StatusIds,
} from '@src/types/claimsTracker';
import { CatalogOption, CatalogOptionType } from '@src/types/catalogOption';

import './index.scss';

const MAX_DETAIL_LENGTH = 150;

// El backend responde ProblemDetails con el código de error como clave de
// `errors` (el `title` es genérico). Se traduce a un mensaje útil para el analista.
const ERROR_MESSAGES: Record<string, string> = {
  'ClaimStatus.InvalidAnalysisTransition':
    'El subestado elegido ya no está disponible desde el estado actual del caso. Se actualizó la pantalla.',
  'ClaimStatus.PendingDocumentsRequired':
    'Seleccione al menos un documento pendiente.',
  'ClaimStatus.InvalidPendingDocument':
    'Uno o más documentos seleccionados ya no están disponibles. Vuelva a abrir el modal e intente de nuevo.',
};

interface Props {
  claimUId: string;
  /** currentStep (nombre de la etapa) para el payload de PUT /status. */
  currentStage: string;
  /** currentStepStatus (nombre del subestado actual) para el payload. */
  currentStatus: string;
  /** País del caso (para cargar el catálogo de documentos pendientes). */
  countryCode: string;
  /** Aseguradora del caso = AccountReference (Purdy = 44), para el catálogo. */
  insurerId: number;
  /** Se invoca tras intentar el cambio (éxito o conflicto) para refrescar estado + historial. */
  onStatusChanged?: () => void;
}

/**
 * HU-06 (CLAIM-3725): botón fijo "Actualizar análisis" + modal "Confirmar
 * Subestado" para cambiar el subestado dentro de la etapa Análisis (Purdy CR).
 * Los destinos se cargan de forma lazy al abrir el modal desde el backend
 * (matriz SubStateFlow). El cambio reutiliza el PUT /status con NewStatus y no
 * recarga la página: el refresco lo coordina el padre vía onStatusChanged.
 */
const UpdateAnalysis: FunctionComponent<Props> = ({
  claimUId,
  currentStage,
  currentStatus,
  countryCode,
  insurerId,
  onStatusChanged,
}): JSX.Element => {
  const [open, setOpen] = useState<boolean>(false);
  const [loadingOptions, setLoadingOptions] = useState<boolean>(false);
  const [optionsError, setOptionsError] = useState<boolean>(false);
  const [transitions, setTransitions] = useState<AnalysisTransitionOption[]>(
    [],
  );
  const [selectedStatusId, setSelectedStatusId] = useState<number | ''>('');
  const [detail, setDetail] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });
  const [documents, setDocuments] = useState<CatalogOption[]>([]);
  const [selectedDocCodes, setSelectedDocCodes] = useState<string[]>([]);
  const [loadingDocs, setLoadingDocs] = useState<boolean>(false);
  const [docsError, setDocsError] = useState<boolean>(false);
  // Descarta respuestas de cargas obsoletas del GET de transiciones si el
  // usuario abre/cierra/reabre el modal rápido (evita pisar la lista vigente).
  const loadGenerationRef = useRef<number>(0);
  const docsGenerationRef = useRef<number>(0);

  const isPendingDocsSelected =
    selectedStatusId === StatusIds.ANALYSIS_PENDING_DOCS;

  const resetModalState = () => {
    setTransitions([]);
    setSelectedStatusId('');
    setDetail('');
    setOptionsError(false);
    setDocuments([]);
    setSelectedDocCodes([]);
    setDocsError(false);
  };

  const loadPendingDocuments = async () => {
    setLoadingDocs(true);
    setDocsError(false);
    const generation = ++docsGenerationRef.current;
    try {
      const options = await new ClaimsTrackerClient().getCatalogOptions(
        CatalogOptionType.PENDING_DOC,
        countryCode,
        insurerId,
      );
      if (generation !== docsGenerationRef.current) {
        return;
      }
      if (!options || options.length === 0) {
        setDocsError(true);
        setDocuments([]);
        return;
      }
      setDocuments(options);
    } catch (error) {
      if (generation !== docsGenerationRef.current) {
        return;
      }
      console.error('Error loading pending documents catalog:', error);
      setDocsError(true);
      setDocuments([]);
    } finally {
      if (generation === docsGenerationRef.current) {
        setLoadingDocs(false);
      }
    }
  };

  const handleSubstateChange = (statusId: number) => {
    setSelectedStatusId(statusId);
    setSelectedDocCodes([]);
    if (statusId === StatusIds.ANALYSIS_PENDING_DOCS) {
      loadPendingDocuments();
    } else {
      setDocuments([]);
      setDocsError(false);
    }
  };

  // Lazy: los destinos permitidos se piden solo al abrir el modal, no en el
  // render de la pantalla.
  const handleOpen = async () => {
    resetModalState();
    setOpen(true);
    setLoadingOptions(true);
    const generation = ++loadGenerationRef.current;
    try {
      const result = await new ClaimsTrackerClient().getAnalysisTransitions(
        claimUId,
      );
      // Ignora la respuesta si llegó una carga posterior (reapertura rápida).
      if (generation !== loadGenerationRef.current) {
        return;
      }
      setTransitions(result?.transitions ?? []);
    } catch (error) {
      if (generation !== loadGenerationRef.current) {
        return;
      }
      console.error('Error loading analysis transitions:', error);
      setOptionsError(true);
      setTransitions([]);
    } finally {
      if (generation === loadGenerationRef.current) {
        setLoadingOptions(false);
      }
    }
  };

  const handleClose = () => {
    if (saving) {
      return;
    }
    setOpen(false);
  };

  const handleDetailChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    // Recorta a 150 aunque el usuario pegue un texto más largo.
    setDetail(event.target.value.slice(0, MAX_DETAIL_LENGTH));
  };

  const pendingDocsInvalid =
    isPendingDocsSelected &&
    (loadingDocs || docsError || selectedDocCodes.length === 0);

  const isConfirmDisabled =
    saving || loadingOptions || selectedStatusId === '' || pendingDocsInvalid;

  const handleConfirm = async () => {
    if (isConfirmDisabled) {
      return;
    }

    setSaving(true);
    try {
      const data = {
        CurrentStage: currentStage,
        CurrentStatus: currentStatus,
        UserId: Number(localStorage.getItem('userId')),
        ReasonStatusChange: detail.trim(),
        NewStatus: {
          StageId: StageIds.ANALYSIS,
          StatusId: Number(selectedStatusId),
        },
        ...(isPendingDocsSelected
          ? { PendingDocuments: selectedDocCodes }
          : {}),
      };

      // Usamos el cliente directo (no el wrapper de servicio) para que un rechazo
      // del backend (transición inválida / concurrencia) llegue como excepción y
      // podamos mostrar el mensaje. La sync de etapa a CPE no aplica a subestados
      // de Análisis, por lo que no se pierde nada respecto al flujo del servicio.
      await new ClaimsTrackerClient().putClaimStatus(data, claimUId);

      setOpen(false);
      setFeedback({
        open: true,
        message: 'Subestado actualizado correctamente.',
        severity: 'success',
      });

      onStatusChanged?.();
    } catch (error) {
      // El backend rechaza transiciones inválidas o fuera de la matriz (incluye
      // el caso de concurrencia: otro analista ya cambió el subestado). El modal
      // permanece abierto con la selección para que el analista reintente, y se
      // refresca el estado real para que vea el subestado vigente.
      const axiosError = error as AxiosError;
      const problem = axiosError?.response?.data as
        | { errors?: Record<string, string[]>; detail?: string; title?: string }
        | undefined;
      const errorCode = Object.keys(problem?.errors ?? {})[0];
      const description =
        (errorCode ? ERROR_MESSAGES[errorCode] : undefined) ??
        problem?.detail ??
        problem?.title ??
        'No se pudo actualizar el subestado. Intente nuevamente.';

      console.error('Error updating analysis substate:', error);
      setFeedback({ open: true, message: description, severity: 'error' });
      onStatusChanged?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    // Fragment (no Box) para que el botón sea hermano directo de los demás de
    // la barra de acciones y se alinee igual. El Dialog se monta por portal; el
    // Snackbar NO (queda dentro de la barra y hereda su text-align: right), por
    // eso el Alert fija textAlign: 'left'.
    <>
      <Button
        className="update-analysis__trigger"
        text="Actualizar análisis"
        type="button"
        color="primary"
        // Mismo estilo que los demás botones de la barra (customStyle en
        // sections) para igualar altura y padding, y quedar alineado.
        style={{ padding: '8px 16px', height: 'auto' }}
        onClick={handleOpen}
      />

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        classes={{ paper: 'update-analysis__dialog' }}
      >
        <DialogTitle className="update-analysis__dialog-title">
          Actualizar análisis
        </DialogTitle>
        <DialogContent className="update-analysis__dialog-content">
          <p className="update-analysis__question">
            ¿A qué subestado desea mover el análisis?
          </p>
          {loadingOptions ? (
            <div className="update-analysis__loading">
              <CircularProgress size={24} />
            </div>
          ) : optionsError ? (
            <Alert severity="error">
              No se pudieron cargar los subestados disponibles.
            </Alert>
          ) : (
            <>
              <FormControl
                fullWidth
                size="small"
                sx={{ mt: 1, backgroundColor: '#ffffff' }}
              >
                <InputLabel id="update-analysis-target">Subestado</InputLabel>
                <Select
                  labelId="update-analysis-target"
                  label="Subestado"
                  value={selectedStatusId === '' ? '' : selectedStatusId}
                  onChange={(event) =>
                    handleSubstateChange(Number(event.target.value))
                  }
                >
                  {transitions.map((option) => (
                    <MenuItem key={option.statusId} value={option.statusId}>
                      {option.statusTitle}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* HU-07 (CLAIM-3726): documentos pendientes, solo para el destino
                  "Requisitos Pendientes". Multi-selección; el catálogo se carga
                  lazy al elegir ese destino. */}
              {isPendingDocsSelected &&
                (loadingDocs ? (
                  <div className="update-analysis__loading">
                    <CircularProgress size={22} />
                  </div>
                ) : docsError ? (
                  <Alert severity="error" sx={{ mt: 1.5 }}>
                    No se pudieron cargar los documentos pendientes.
                  </Alert>
                ) : (
                  <FormControl
                    fullWidth
                    size="small"
                    sx={{ mt: 1.5, backgroundColor: '#ffffff' }}
                  >
                    <InputLabel id="update-analysis-docs">
                      Documentos pendientes
                    </InputLabel>
                    <Select
                      labelId="update-analysis-docs"
                      multiple
                      value={selectedDocCodes}
                      onChange={(event) =>
                        setSelectedDocCodes(
                          typeof event.target.value === 'string'
                            ? event.target.value.split(',')
                            : event.target.value,
                        )
                      }
                      input={<OutlinedInput label="Documentos pendientes" />}
                      renderValue={(selected) =>
                        documents
                          .filter((doc) => selected.includes(doc.code))
                          .map((doc) => doc.description)
                          .join(', ')
                      }
                    >
                      {documents.map((doc) => (
                        <MenuItem key={doc.code} value={doc.code}>
                          <Checkbox
                            checked={selectedDocCodes.includes(doc.code)}
                          />
                          <ListItemText primary={doc.description} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ))}

              <TextField
                className="update-analysis__detail"
                label="Detalle (opcional)"
                value={detail}
                onChange={handleDetailChange}
                multiline
                fullWidth
                size="small"
                sx={{ mt: 1.5 }}
                inputProps={{ maxLength: MAX_DETAIL_LENGTH }}
                helperText={`${detail.length}/${MAX_DETAIL_LENGTH}`}
              />
            </>
          )}
        </DialogContent>
        <DialogActions className="update-analysis__dialog-actions">
          <Button
            text="Cancelar"
            type="button"
            color="secondary"
            variant="outlined"
            size="small"
            disabled={saving}
            onClick={handleClose}
          />
          <Button
            text={saving ? 'Guardando...' : 'Aceptar'}
            type="button"
            color="primary"
            size="small"
            disabled={isConfirmDisabled}
            onClick={handleConfirm}
          />
        </DialogActions>
      </Dialog>

      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
          severity={feedback.severity}
          sx={{
            width: '100%',
            maxWidth: 420,
            alignItems: 'center',
            textAlign: 'left',
          }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UpdateAnalysis;
